import { useCallback, useEffect, useRef } from 'react';
import { useSarahVoice } from './useSarahVoice';
import { useInterviewStore } from '../store';

export function useSpeechSynthesis() {
  const config = useInterviewStore((state) => state.interviewConfig);
  const isSpeakingRef = useRef(false);
  const speakQueueRef = useRef<string[]>([]);

  const { speak: sarahSpeak, stop: sarahStop, isPlaying } = useSarahVoice({
    config,
    onStart: () => {
      isSpeakingRef.current = true;
    },
    onEnd: () => {
      isSpeakingRef.current = false;
      processQueue();
    },
    onError: (error) => {
      console.error('Speech synthesis error:', error);
      isSpeakingRef.current = false;
      processQueue();
    }
  });

  const processQueue = useCallback(async () => {
    if (speakQueueRef.current.length > 0 && !isSpeakingRef.current) {
      const nextText = speakQueueRef.current.shift();
      if (nextText) {
        try {
          await sarahSpeak(nextText);
        } catch (error) {
          console.error('Error processing speech queue:', error);
          // Continue processing queue even if there's an error
          processQueue();
        }
      }
    }
  }, [sarahSpeak]);

  const speak = useCallback((text: string) => {
    if (!text) return;

    // If speech synthesis is not supported, just skip it
    if (!window.speechSynthesis) {
      console.warn('Speech synthesis is not supported in this browser');
      return;
    }

    speakQueueRef.current.push(text);
    if (!isSpeakingRef.current) {
      processQueue();
    }
  }, [processQueue]);

  const stop = useCallback(() => {
    sarahStop();
    speakQueueRef.current = [];
    isSpeakingRef.current = false;
  }, [sarahStop]);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return {
    speak,
    stop,
    isSpeaking: () => isPlaying()
  };
}